<template>
  <div>
    <div class="flex items-center justify-between mb-1">
      <span class="text-base-content/60"
        >{{ caseDef.studies.length }} stud{{
          caseDef.studies.length === 1 ? "y" : "ies"
        }}</span
      >
      <span
        v-if="editable"
        class="text-xs text-base-content/40 flex items-center gap-1"
      >
        <Icon icon="lucide:grip-vertical"></Icon> drag to reorder
      </span>
    </div>

    <draggable
      v-model="props.caseDef.studies"
      group="studies"
      item-key="uid"
      :disabled="!editable"
      handle=".handle"
    >
      <template #item="{ element: study, elementIndex: studyIndex }">
        <div
          :class="{ 'study-removed': isStudyRemoved(study.uid) }"
          class="study-group px-2 py-1 mb-2"
        >
          <div class="study-row flex items-center gap-1 px-1 mb-1">
            <div
              class="handle shrink-0"
              :class="editable ? 'is-draggable' : 'opacity-20'"
            >
              <Icon icon="lucide:grip-vertical"></Icon>
            </div>
            <div
              class="font-medium flex-1 min-w-0 truncate dim"
              :class="{ 'line-through': isStudyRemoved(study.uid) }"
            >
              {{ study.studyDescription || `Study ${studyIndex + 1}` }}
            </div>
            <span
              v-if="isStudyRemoved(study.uid)"
              class="badge badge-error shrink-0"
              title="Marked for removal"
            >
              excluded
            </span>
            <button
              v-if="editable"
              class="btn btn-xs btn-remove shrink-0"
              @click="emits('toggleStudyRemoved', study.uid)"
            >
              {{
                isStudyRemoved(study.uid) ? "Include study" : "Exclude study"
              }}
            </button>
          </div>
          <draggable
            v-model="study.series"
            group="series"
            item-key="uid"
            :disabled="!editable"
            handle=".handle"
          >
            <template #item="{ element: series, elementIndex: seriesIndex }">
              <div
                class="series-row flex items-center gap-2 ml-4 pl-2 pr-1 py-1 mb-1 relative cursor-pointer"
                :class="{
                  selected: isSelected(series),
                  'series-removed': isSeriesRemoved(series.uid),
                  'fully-anonymized': seriesFullyAnonymized(series),
                }"
                @click="selectSeries(series)"
              >
                <!-- Currently-viewing accent bar -->
                <div
                  v-if="isSelected(series)"
                  class="absolute left-0 top-0 bottom-0 w-1 bg-primary dim"
                ></div>

                <div
                  class="handle shrink-0"
                  :class="editable ? 'is-draggable' : 'opacity-20'"
                >
                  <Icon icon="lucide:grip-vertical"></Icon>
                </div>

                <div
                  class="flex-1 min-w-0 truncate dim"
                  :class="{
                    'font-semibold': isSelected(series),
                    'line-through': isSeriesRemoved(series.uid),
                  }"
                >
                  {{ series.seriesDescription || `Series ${seriesIndex + 1}` }}
                  <span v-if="series.displaySet" class="opacity-60">
                    · {{ series.displaySet.label }}
                  </span>
                </div>

                <!-- Status badges -->
                <div class="flex items-center gap-1 shrink-0 status-badges">
                  <!-- <span
                    v-if="isSelected(series)"
                    class="badge badge-viewing"
                    title="Currently displayed in the viewer"
                  >
                    <Icon icon="bx:bxs-right-arrow" class="text-[0.6rem]"></Icon>
                    Viewing
                  </span> -->

                  <span
                    class="badge badge-neutral"
                    :title="`${seriesFrameCount(series)} image${
                      seriesFrameCount(series) === 1 ? '' : 's'
                    } across ${series.instances.length} instance${
                      series.instances.length === 1 ? '' : 's'
                    }`"
                  >
                    {{ seriesFrameCount(series) }} img
                  </span>

                  <!-- Images excluded one at a time in the viewer. Only worth
                       showing while the series itself is still included —
                       otherwise the "excluded" badge below already says it. -->
                  <span
                    v-if="
                      !isSeriesRemoved(series.uid) &&
                      seriesExcludedFrameCount(series) > 0
                    "
                    class="badge badge-error"
                    :title="`${seriesExcludedFrameCount(series)} of ${seriesFrameCount(
                      series
                    )} images in this series are excluded from export.`"
                  >
                    {{ seriesExcludedFrameCount(series) }} of
                    {{ seriesFrameCount(series) }} excluded
                  </span>

                  <span
                    v-if="seriesAnonymizedInstances(series).length > 0"
                    class="badge badge-success"
                    :title="`${seriesAnonymizedInstances(series).length} of ${series.instances.length} anonymized`"
                  >
                    <Icon icon="bx:bx-check"></Icon>
                    {{ seriesAnonymizedInstances(series).length }}/{{
                      series.instances.length
                    }}
                  </span>

                  <span
                    v-if="seriesQuarantined(series) > 0"
                    class="badge badge-warning"
                    :title="`${seriesQuarantined(series)} image(s) quarantined`"
                  >
                    <Icon icon="feather:alert-triangle"></Icon>
                    {{ seriesQuarantined(series) }}
                  </span>

                  <span
                    v-if="anonymizationErrors(series) > 0"
                    class="badge badge-error"
                    :title="`${anonymizationErrors(series)} anonymization error(s). See logs.`"
                  >
                    <Icon icon="feather:alert-triangle"></Icon>
                    {{ anonymizationErrors(series) }}
                  </span>

                  <span
                    v-if="isSeriesRemoved(series.uid)"
                    class="badge badge-error"
                    title="Marked for removal"
                  >
                    excluded
                  </span>

                  <div title="Reviewed">
                    <Icon
                      v-if="series.hasBeenSelected"
                      icon="feather:eye"
                      class="text-base-content/50"
                    ></Icon>
                  </div>
                </div>

                <button
                  v-if="editable"
                  @click.stop="emits('toggleSeriesRemoved', series.uid)"
                  class="btn btn-xs btn-remove shrink-0"
                >
                  {{ seriesButtonLabel(series) }}
                </button>
              </div>
            </template>
          </draggable>
        </div>
      </template>
    </draggable>

    <!-- Legend -->
    <div
      v-if="caseDef.studies.length > 0"
      class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-2 border-t border-base-300 text-xs text-base-content/50"
    >
      <span class="flex items-center gap-1"
        ><span class="badge badge-success"
          ><Icon icon="bx:bx-check"></Icon
        ></span>
        anonymized</span
      >
      <span class="flex items-center gap-1"
        ><span class="badge badge-warning"
          ><Icon icon="feather:alert-triangle"></Icon
        ></span>
        quarantined</span
      >
      <span class="flex items-center gap-1"
        ><span class="badge badge-error"
          ><Icon icon="feather:alert-triangle"></Icon
        ></span>
        error</span
      >
      <span class="flex items-center gap-1"
        ><Icon icon="feather:eye" class="text-base-content/50"></Icon>
        reviewed</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import CaseDef from "./types/CaseDef";
import Series from "./types/Series";
import draggable from "vuedraggable";
import { computed } from "vue";
import { Icon } from "@iconify/vue";

const emits = defineEmits([
  "select-series",
  "toggleSeriesRemoved",
  "toggleStudyRemoved",
]);

const props = defineProps<{
  caseDef: CaseDef;
  selectable?: boolean;
  selectedSeries: Series | null;
  editable: boolean;
}>();

const markedForRemoval = computed(() => props.caseDef.markedForRemoval);

function isStudyRemoved(uid: string) {
  return markedForRemoval.value?.studyUIDs.includes(uid);
}

function isSeriesRemoved(uid: string) {
  return markedForRemoval.value?.seriesUIDs.includes(uid);
}

function isSelected(series: Series) {
  // Keyed on the app-level uid, not the DICOM one: a split series has several
  // parts sharing one SeriesInstanceUID, and matching on that highlights all of
  // them at once. See display-sets.ts.
  return series.uid === props.selectedSeries?.uid;
}
function selectSeries(series: Series) {
  emits("select-series", series);
}
function seriesAnonymizedInstances(series: Series) {
  return series.instances.filter((i) => i.instanceData.anonymizedDicomData);
}
function seriesFrameCount(series: Series) {
  // Count displayable frames, not instances: a multiframe instance contributes
  // all of its frames.
  return series.instances.reduce(
    (sum, i) => sum + (i.frames?.length ?? i.numberOfFrames ?? 1),
    0
  );
}
/**
 * Frames excluded individually in the viewer. Counted in frames, not instances,
 * so it lines up with the "N img" badge next to it: one excluded multiframe
 * instance takes all of its frames out of the export.
 */
function seriesExcludedFrameCount(series: Series) {
  const removed = markedForRemoval.value?.instanceUIDs;
  if (!removed || removed.length === 0) return 0;
  return series.instances.reduce(
    (sum, i) =>
      removed.includes(i.uid)
        ? sum + (i.frames?.length ?? i.numberOfFrames ?? 1)
        : sum,
    0
  );
}
function seriesButtonLabel(series: Series) {
  if (isSeriesRemoved(series.uid)) return "Include";
  // "Exclude" would read as a no-op once part of the series is already out.
  return seriesExcludedFrameCount(series) > 0 ? "Exclude all" : "Exclude";
}
function seriesQuarantined(series: Series) {
  return series.instances.filter((i) => i.quarantine).length;
}
function seriesFullyAnonymized(series: Series) {
  return seriesAnonymizedInstances(series).length === series.instances.length;
}
function anonymizationErrors(series: Series) {
  const seriesErrorCount = series.instances.reduce((errCount, instance) => {
    if (instance.anonymizationError) {
      return (errCount += 1);
    }
    return errCount;
  }, 0);
  return seriesErrorCount;
}
</script>

<style scoped>
.btn {
  font-size: 0.8125rem;
  line-height: 1.25rem;
}
.btn-remove {
  color: var(--color-error);
}

.study-group {
  border: 1px solid color-mix(in oklab, var(--color-base-300) 70%, transparent);
  border-radius: 0.375rem;
  background-color: color-mix(in oklab, var(--color-base-300) 18%, transparent);
}
.study-row {
  border-bottom: 1px solid color-mix(in oklab, var(--color-base-300) 60%, transparent);
  padding-bottom: 0.375rem;
}

/* Removed studies/series read as recessed and inactive rather than struck out */
.study-removed .dim,
.series-removed .dim{
  opacity: 0.45;
}

.series-row.fully-anonymized {
  background-color: color-mix(in oklab, var(--color-success) 12%, transparent);
}
.series-row:hover {
  background-color: color-mix(in oklab, var(--color-base-300) 45%, transparent);
}
.series-row.selected {
  background-color: color-mix(in oklab, var(--color-primary) 12%, transparent);
}
.series-row.selected:hover {
  background-color: color-mix(in oklab, var(--color-primary) 16%, transparent);
}

.handle {
  display: flex;
  align-items: center;
  color: var(--color-base-content);
  opacity: 0.35;
}
.handle.is-draggable {
  cursor: grab;
  opacity: 0.6;
}
.handle.is-draggable:hover {
  opacity: 1;
}

/* Compact status badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.6875rem;
  line-height: 1;
  padding: 0.15rem 0.35rem;
  border-radius: 0.25rem;
  white-space: nowrap;
  border: 1px solid transparent;
}
.badge-neutral {
  color: var(--color-base-content);
  background-color: color-mix(in oklab, var(--color-base-300) 50%, transparent);
}
.badge-viewing {
  color: var(--color-primary);
  border-color: color-mix(in oklab, var(--color-primary) 40%, transparent);
  background-color: color-mix(in oklab, var(--color-primary) 12%, transparent);
}
.badge-success {
  color: var(--color-success);
  background-color: color-mix(in oklab, var(--color-success) 15%, transparent);
}
.badge-warning {
  color: var(--color-warning);
  background-color: color-mix(in oklab, var(--color-warning) 18%, transparent);
}
.badge-error {
  color: var(--color-error);
  background-color: color-mix(in oklab, var(--color-error) 15%, transparent);
}
</style>
